import { useEffect, useState, useRef } from "react";
import { Input, Radio, Form, Row, Col, DatePicker, Button, Tooltip, Rate } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import TextArea from "antd/lib/input/TextArea";
import dayjs from "dayjs";
import { IDriverByIdResponse } from "../../../../types/driver";
import constants from "../../../../constants";
import SelectStateOption from "../../../selectOption/SelectStateOption";
import { UploadImage } from "../../../axios/imageUpload";
import imageCompression from "browser-image-compression";
import { handleImageSize } from "../../../../utils/handleImageSize";

const defaultImage: string =
  require("../../../../assets/logo/defaultImage.svg").default;

type PersonalDetailsProps = {
  data?: IDriverByIdResponse;
  setImageFile?: any;
  personalDetailsResponse?: any;
};

const imageUploaderStyle = {
  width: "9rem",
  height: "7rem",
  borderRadius: "5%",
  border: "2px Solid #cbe3f7",
};

const inputFileLabelStyle: React.CSSProperties = {
  padding: "8px 20px",
  background: "#3e4685",
  borderRadius: "5px",
  border: "none",
  color: "white",
  cursor: "pointer",
  display: "inline-block",
  textAlign: "center",
  fontSize: "14px",
  transition: "background-color 0.3s ease",
};

const getRatingColor = (rating: number) => {
  if (rating >= 4) return "#52c41a";
  if (rating >= 2.5) return "#faad14";
  return "#ff4d4f";
};

const PersonalDetails = (props: PersonalDetailsProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null); // <-- Added ref
  const [isHovered, setIsHovered] = useState(false);
  const [form] = Form.useForm();
  const { data, setImageFile, personalDetailsResponse } = props;

  const [img, setImg] = useState({
    image_file: "",
    image_preview: "",
  });

  const imgFileHandler = async (e: any) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      const resultImage = await handleImageSize(file);

      if (resultImage !== undefined) {
        const compressedFile: File = resultImage;
        const image_as_base64 = URL.createObjectURL(compressedFile);

        let fd = new FormData();
        fd.append("image", compressedFile);

        UploadImage(fd)
          .then((res) => {
            setImg({
              image_preview: image_as_base64,
              image_file: res.data,
            });
            setImageFile(res.data.url || res.data);
          })
          .catch((err) => {
            console.log(err);
          });
      }
    }
  };

  useEffect(() => {
    setImg({
      image_file: `${data?.data.image}`,
      image_preview: `${data?.data.image}`,
    });
    setImageFile(data?.data.image);
  }, [data?.data.image]);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    dob: "",
    gender: "",
    phoneNumber: "",
    email: "",
    drivingExperience: "",
    address: "",
    cabStand: "",
    state: "",
    dateOfJoining: "",
  });

  const handleFieldChange = (fieldName: any, value: any) => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      [fieldName]: value,
    }));
    let res = Object.values(formData).every((value) => value.trim() !== "");
    personalDetailsResponse?.(res);
  };

  // Every check lives in the Form.Item rules below, so the message always matches the value
  // actually in the field, and the parent's validateFields() can stop a save on it.
  const lettersOnly = (value?: string) => (value ?? "").replace(/[^A-Za-z ]/g, "");
  const digitsOnly = (value?: string, max = 10) => (value ?? "").replace(/\D/g, "").slice(0, max);
  const latestAdultDob = dayjs().subtract(18, "year").endOf("day");

  const handleDateOfJoiningChange = (date: any) => {
    handleFieldChange("dateOfJoining", date);
  };

  const handleStateResponse = (response: any) => {
    handleFieldChange("state", response);
  };

  const handleCabstand = (e: any) => {
    handleFieldChange("cabStand", e.target.value);
  };

  const isValidDate = (dateString: any) => {
    return dayjs(dateString, "DD/MM/YYYY", true).isValid();
  };

  return (
    <>
      <Form
        name="personalDetailForm"
        form={form}
        initialValues={{
          firstName: data?.data.firstName,
          lastName: data?.data.lastName,
          gender: data?.data.gender,
          phoneNumber: data?.data.phoneNumber,
          email: data?.data.email,
          dob:
            data?.data.dob && isValidDate(data?.data.dob)
              ? dayjs(data?.data.dob, "DD/MM/YYYY")
              : "",
          address: data?.data.address,
          cabStand: data?.data.cabStand,
          state: data?.data.state,
          drivingExperience: data?.data.drivingExperience,
          image: data?.data?.image,
          dateOfJoining: data?.data.isVerified
            ? data?.data.dateOfJoining
              ? dayjs(dayjs(data?.data.dateOfJoining), "DD/MM/YYYY")
              : ""
            : "",
        }}
      >
        <Row gutter={[32, 22]}>
  <Col span={12}>
    <Row>
      {/* Image + Upload Button */}
      <Col span={24} style={{ marginBottom: "24px" }}> {/* Add bottom margin here */}
        <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
          <img
            src={img.image_preview}
            onError={(e) => {
              (e.target as HTMLSourceElement).src = defaultImage;
            }}
            alt="Profile Img"
            style={{ ...imageUploaderStyle, flexShrink: 0 }}
          />
          <div>
            <input
              type="file"
              ref={fileInputRef}
              accept="image/*"
              style={{ display: "none" }}
              onChange={imgFileHandler}
            />
            <Button
              htmlType="button"
              onClick={() => fileInputRef.current?.click()}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              style={{
                ...inputFileLabelStyle,
                backgroundColor: isHovered ? "#4c9eff" : "#3e4685",
                color: "white",
              }}
            >
              Upload Image
            </Button>
          </div>
        </div>
      </Col>

      {/* First Name Field */}
      <Col span={24}>
        <label>First Name</label>
        <Form.Item
          name="firstName"
          normalize={lettersOnly}
          rules={[
            { required: true, whitespace: true, message: "First name is required" },
            { pattern: /^[A-Za-z ]+$/, message: "First name can only have letters" },
          ]}
        >
          <Input onChange={(e) => handleFieldChange("firstName", e.target.value)} />
        </Form.Item>
      </Col>


              <Col span={24}>
                <label>Last Name</label>
                <Form.Item
                  name="lastName"
                  normalize={lettersOnly}
                  rules={[
                    // The last name is optional, but when it is typed it has to be a name
                    { pattern: /^[A-Za-z ]*$/, message: "Last name can only have letters" },
                  ]}
                >
                  <Input onChange={(e) => handleFieldChange("lastName", e.target.value)} />
                </Form.Item>
              </Col>

              <Col span={24}>
                <label>Date of Birth</label>
                <Form.Item
                  name="dob"
                  rules={[
                    { required: true, message: "Date of Birth is required" },
                    {
                      // Full date, not just the year: born 10-12-2008 is not 18 on 09-10-2026.
                      validator: (_, value) =>
                        !value || dayjs().diff(value, "year") >= 18
                          ? Promise.resolve()
                          : Promise.reject(new Error("Driver must be at least 18 years old")),
                    },
                  ]}
                >
                  <DatePicker
                    className="w-100"
                    format={"DD-MM-YYYY"}
                    defaultPickerValue={latestAdultDob}
                    disabledDate={(current) => current && current.isAfter(latestAdultDob)}
                    onChange={(date) => handleFieldChange("dob", date)}
                  />
                </Form.Item>
              </Col>

              <Col span={24}>
                <label>Gender</label>
                <Form.Item
                  name="gender"
                  rules={[{ required: true, message: "Gender is required" }]}
                >
                  <Radio.Group
                    name="radiogroup"
                    style={{ color: "#3E4685" }}
                    defaultValue={false}
                    onChange={(e) => handleFieldChange("gender", e.target.value)}
                  >
                    <Radio className="ms-3" value={"MALE"}>
                      Male
                    </Radio>
                    <Radio value={"FEMALE"}>Female</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>

              <Col span={24}>
                <label>Phone Number</label>
                <Form.Item
                  name="phoneNumber"
                  normalize={(value) => digitsOnly(value, 10)}
                  rules={[
                    { required: true, message: "Phone number is required" },
                    { pattern: /^[6-9]\d{9}$/, message: "Enter a valid 10 digit mobile number" },
                  ]}
                >
                  <Input
                    inputMode="numeric"
                    maxLength={10}
                    onChange={(e) => handleFieldChange("phoneNumber", e.target.value)}
                  />
                </Form.Item>
              </Col>

              <Col span={24}>
                <label>Email</label>
                <Form.Item
                  name="email"
                  normalize={(value) => (value ?? "").trim()}
                  rules={[
                    { required: true, message: "Email is required" },
                    { type: "email", message: "Enter a valid email address" },
                  ]}
                >
                  <Input onChange={(e) => handleFieldChange("email", e.target.value)} />
                </Form.Item>
              </Col>
            </Row>
          </Col>

          <Col span={12}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
            <label>Driver's Cab Stand</label>
            <Tooltip 
              title={
                <div>
                  <div style={{ marginBottom: '8px', fontWeight: 'bold' }}>English:</div>
                  <div style={{ marginBottom: '12px' }}>Enter the place from where the driver usually starts the day or keeps the cab parked.</div>
                  <div style={{ marginBottom: '8px', fontWeight: 'bold' }}>Hindi:</div>
                  <div>वह जगह दर्ज करें जहाँ से ड्राइवर आमतौर पर दिन की शुरुआत करता है या गाड़ी खड़ी रखता है।</div>
                </div>
              }
            >
              <InfoCircleOutlined style={{ marginLeft: "4px", color: "rgba(0,0,0,.45)" }} />
            </Tooltip>
          </div>
            <Form.Item
              name="cabStand"
              rules={[{ required: true, whitespace: true, message: "Cab stand is required" }]}
            >
              <Input
                onChange={(e) => handleCabstand(e)}
              />
            </Form.Item>
            
            <label>Address</label>
            <Form.Item
              name="address"
              rules={[{ required: true, whitespace: true, message: "Address is required" }]}
            >
              <TextArea
                rows={3}
                onChange={(e) => handleFieldChange("address", e.target.value)}
              />
            </Form.Item>

            <label>State</label>
<div style={{ marginBottom: "24px" }}>
  <SelectStateOption
    disabled={false}
    handleStateResponse={handleStateResponse}
  />
</div>

<label>Date of joining</label>
<Form.Item
  name="dateOfJoining"
  rules={[{ required: true, message: "Date of Joining is required" }]}
>
  <DatePicker
    className="w-100"
    format={"DD-MM-YYYY"}
    disabledDate={(current) => current && current.isAfter(dayjs().endOf("day"))}
    onChange={handleDateOfJoiningChange}
  />
</Form.Item>

<label>Driving Experience (years)</label>
<Form.Item
  name="drivingExperience"
  rules={[
    { required: true, message: "Driving experience is required" },
    {
      validator: (_, value) =>
        value === undefined || value === null || value === "" ||
        (Number(value) >= 1 && Number(value) <= 60)
          ? Promise.resolve()
          : Promise.reject(new Error("Enter valid years of experience (1-60)")),
    },
  ]}
>
  <Input type="number" min={1} max={60} step={0.5} />
</Form.Item>

<label>Driver Rating</label>
<div
  style={{
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "24px",
    padding: "4px 11px",
    border: "2px solid #bbd4ff",
    borderRadius: "6px",
    background: "#fff",
    minHeight: "32px",
  }}
>
  <Rate
    disabled
    allowHalf
    value={data?.data.rating || 0}
    style={{ color: getRatingColor(data?.data.rating || 0) }}
  />
  <span>{(data?.data.rating ?? 0).toFixed(1)} / 5</span>
</div>

          </Col>
        </Row>
      </Form>
    </>
  );
};

export default PersonalDetails;
